import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone, Facebook, Instagram, Linkedin, Heart } from 'lucide-react';
import { subscribeNewsletter } from '../lib/api';
import { useSection, resolveImage } from '../contexts/ContentContext';
import { useToast } from '../hooks/use-toast';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const settings = useSection('settings');
  const footer = useSection('footer');
  const nav = useSection('navigation');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      toast({ title: 'Inscription confirmée !', description: 'Vous recevrez nos prochaines actualités.' });
      setEmail('');
    } catch (err) {
      toast({ title: 'Erreur', description: 'Impossible de s\'inscrire pour le moment.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const year = new Date().getFullYear();
  const copyright = (footer.copyright || '').replace('{year}', year);
  const contact = settings.contact || {};
  const socials = settings.socials || {};
  const menuItems = nav.menuItems || [];
  const logo = resolveImage(settings.logoUrl);

  return (
    <footer className="relative bg-gradient-to-br from-sky-950 via-sky-900 to-emerald-950 text-sky-100 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-20 overflow-hidden">
        <svg viewBox="0 0 1440 120" className="w-full h-full text-white" preserveAspectRatio="none">
          <path fill="currentColor" d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,0L0,0Z" />
        </svg>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              {logo && <img src={logo} alt="Logo" className="w-12 h-12 rounded-full shadow-lg ring-2 ring-white/20 object-cover" />}
              <div>
                <p className="font-display font-extrabold text-lg text-white">{settings.siteName}</p>
                <p className="text-[11px] text-emerald-300 font-medium tracking-wide uppercase">Association loi 1901</p>
              </div>
            </div>
            <p className="text-sky-200 text-sm leading-relaxed mb-6">{footer.description}</p>
            <div className="flex items-center gap-3">
              {socials.facebook && (
                <a href={socials.facebook} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-xl bg-white/10 hover:bg-sky-400/30 flex items-center justify-center transition-colors">
                  <Facebook className="w-4 h-4" />
                </a>
              )}
              {socials.instagram && (
                <a href={socials.instagram} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-xl bg-white/10 hover:bg-sky-400/30 flex items-center justify-center transition-colors">
                  <Instagram className="w-4 h-4" />
                </a>
              )}
              {socials.linkedin && (
                <a href={socials.linkedin} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-xl bg-white/10 hover:bg-sky-400/30 flex items-center justify-center transition-colors">
                  <Linkedin className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>

          <div>
            <h4 className="font-display font-bold text-white mb-4">Navigation</h4>
            <ul className="space-y-2.5 text-sm">
              {menuItems.map((l) => (
                <li key={l.to + l.label}><Link to={l.to} className="hover:text-emerald-300 transition-colors">{l.label}</Link></li>
              ))}
              <li><Link to={nav.ctaLink || '/soutenir'} className="hover:text-emerald-300 transition-colors">{nav.ctaLabel || 'Faire un don'}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display font-bold text-white mb-4">Contact</h4>
            <ul className="space-y-3 text-sm text-sky-200">
              {contact.address && <li className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-0.5 text-emerald-300 flex-shrink-0" /><span className="whitespace-pre-line">{contact.address}</span></li>}
              {contact.email && <li className="flex items-center gap-3"><Mail className="w-4 h-4 text-emerald-300 flex-shrink-0" /><a href={`mailto:${contact.email}`} className="hover:text-emerald-300">{contact.email}</a></li>}
              {contact.phone && <li className="flex items-center gap-3"><Phone className="w-4 h-4 text-emerald-300 flex-shrink-0" /><a href={`tel:${contact.phone.replace(/\s/g, '')}`} className="hover:text-emerald-300">{contact.phone}</a></li>}
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-sky-300">{copyright}</p>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-sky-300">
            <Link to="/mentions-legales" data-testid="footer-link-mentions" className="hover:text-emerald-300 transition-colors">Mentions légales</Link>
            <span className="text-sky-700">·</span>
            <Link to="/politique-confidentialite" data-testid="footer-link-privacy" className="hover:text-emerald-300 transition-colors">Confidentialité</Link>
            <span className="text-sky-700">·</span>
            <Link to="/politique-cookies" data-testid="footer-link-cookies" className="hover:text-emerald-300 transition-colors">Cookies</Link>
            <span className="text-sky-700">·</span>
            <Link to="/admin/login" className="hover:text-emerald-300 transition-colors">Espace admin</Link>
            <span className="flex items-center gap-1.5">
              {(() => {
                const text = footer.designedBy || '';
                const idx = text.indexOf('❤');
                if (idx === -1) return <>{text} <Heart className="w-3.5 h-3.5 text-emerald-400 fill-emerald-400" /></>;
                return (
                  <>
                    {text.slice(0, idx)}
                    <Heart className="w-3.5 h-3.5 text-emerald-400 fill-emerald-400 inline-block" />
                    {text.slice(idx + 1)}
                  </>
                );
              })()}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
